/**
 * @typedef {keyof typeof PERMISSIONS_CODES} PermissionCodes
 */

/**
 * @enum {string}
 */
const PERMISSIONS_CODES = {
  // identity access
  IDENTITY_ACCESS_GET_ANY_RESOURCE: 'aab',
  IDENTITY_ACCESS_GET_OWN_RESOURCE: 'aaa',

  // post validation
  ACCEPT_POST: 'saa',
  REFUSE_POST: 'sag',

  // post deletion
  DELETE_ANY_POST: 'sab',
  DELETE_OWN_POST: 'sac',
  DELETE_PAST_POST: 'sad',

  // get post
  GET_ANY_POST: 'sae',
  GET_OWN_POST: 'saf',
  GET_ANY_LEAVE_POST: 'sao',
  GET_OWN_LEAVE_POST: 'san',
  GET_ANY_REMOTE_WORKING_POST: 'sas',
  GET_OWN_REMOTE_WORKING_POST: 'sar',

  // post creation
  CREATE_ANY_NEW_REMOTE_WORKING_POST: 'saq',
  CREATE_OWN_NEW_REMOTE_WORKING_POST: 'sap',
  CREATE_ANY_NEW_LEAVE_POST: 'sam',
  CREATE_OWN_NEW_LEAVE_POST: 'sal',
  CREATE_POST_ANYTIME: 'say',

  // configuration
  GET_CONFIGURATION: 'saj',
  UPDATE_CONFIGURATION: 'sak',

  // resource
  GET_ANY_RESOURCE: 'sav',
  GET_OWN_RESOURCE: 'saw',
  UPDATE_ANY_RESOURCE: 'sax',

  // post update (unused in this version)
  UPDATE_ANY_REMOTE_WORKING_POST: 'sau',
  UPDATE_OWN_REMOTE_WORKING_POST: 'sat',

  // misc (backend use only for now)
  REGISTER_NEW_CALENDAR: 'sah',
  UPDATE_HOLIDAY_LIST: 'sai',
};

/**
 * @enum {string}
 */
export const PERMISSIONS = {
  IDENTITY_ACCESS_GET_ANY_RESOURCE: 'IDENTITY_ACCESS_GET_ANY_RESOURCE',
  IDENTITY_ACCESS_GET_OWN_RESOURCE: 'IDENTITY_ACCESS_GET_ANY_RESOURCE',
  ACCEPT_POST: 'ACCEPT_POST',
  REFUSE_POST: 'REFUSE_POST',
  DELETE_ANY_POST: 'DELETE_ANY_POST',
  DELETE_OWN_POST: 'DELETE_OWN_POST',
  DELETE_PAST_POST: 'DELETE_PAST_POST',
  GET_ANY_POST: 'GET_ANY_POST',
  GET_OWN_POST: 'GET_OWN_POST',
  GET_ANY_LEAVE_POST: 'GET_ANY_LEAVE_POST',
  GET_OWN_LEAVE_POST: 'GET_OWN_LEAVE_POST',
  GET_ANY_REMOTE_WORKING_POST: 'GET_ANY_REMOTE_WORKING_POST',
  GET_OWN_REMOTE_WORKING_POST: 'GET_OWN_REMOTE_WORKING_POST',
  CREATE_ANY_NEW_REMOTE_WORKING_POST: 'CREATE_ANY_NEW_REMOTE_WORKING_POST',
  CREATE_OWN_NEW_REMOTE_WORKING_POST: 'CREATE_OWN_NEW_REMOTE_WORKING_POST',
  CREATE_ANY_NEW_LEAVE_POST: 'CREATE_ANY_NEW_LEAVE_POST',
  CREATE_OWN_NEW_LEAVE_POST: 'CREATE_OWN_NEW_LEAVE_POST',
  CREATE_POST_ANYTIME: 'CREATE_POST_ANYTIME',
  GET_CONFIGURATION: 'GET_CONFIGURATION',
  UPDATE_CONFIGURATION: 'UPDATE_CONFIGURATION',
  GET_ANY_RESOURCE: 'GET_ANY_RESOURCE',
  GET_OWN_RESOURCE: 'GET_OWN_RESOURCE',
  UPDATE_ANY_RESOURCE: 'UPDATE_ANY_RESOURCE',
  UPDATE_ANY_REMOTE_WORKING_POST: 'UPDATE_ANY_REMOTE_WORKING_POST',
  UPDATE_OWN_REMOTE_WORKING_POST: 'UPDATE_OWN_REMOTE_WORKING_POST',
  REGISTER_NEW_CALENDAR: 'REGISTER_NEW_CALENDAR',
  UPDATE_HOLIDAY_LIST: 'UPDATE_HOLIDAY_LIST',
};

/**
 * Splits a string of concatenated permission codes into an array of individual codes.
 * Each permission code is 3 characters long.
 *
 * @param {string} str
 * @returns {Array<string>}
 */
const getUserPermissionCodes = (str) => {
  const result = [];
  if (str) {
    for (let i = 0; i < str.length; i += 3) {
      result.push(str.slice(i, i + 3));
    }
  }
  return result;
};

/**
 * Checks if a user has a specific permission based on their permissions string.
 *
 * @param {string} accessPermission
 * @param {string} userPermissions
 * @returns {Boolean}
 */
const hasPermission = (accessPermission, userPermissions) => {
  const permissionCode = PERMISSIONS_CODES[accessPermission];
  if (!permissionCode) return false;

  const userPermissionsArray = getUserPermissionCodes(userPermissions);
  return userPermissionsArray.includes(permissionCode);
};

export default hasPermission;
